
import { defineComponent, onMounted, reactive } from 'vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { useFormData } from '@/common/composable/useFormData'
import { OnTheWayPayload } from '@/core/types/orders.types'
import { TripComment } from '@/new-services/types/tms'
import { FTL } from '@/core/realisations/FTL.realisation'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

export default defineComponent({
    components: {
        Modal,
        FileReader,
        NewSelect,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        orderId: {
            type: String,
            required: true,
        },
        ftlType: {
            type: Number,
            default: 0,
        },
        isComment: {
            type: Boolean,
            default: false,
        },
        stateId: {
            type: Number,
            default: null,
        },
    },
    emits: ['cancel', 'submit'],
    setup(props, { emit }) {
        const { getFormData } = useFormData()

        const payload = reactive({
            stamp: [] as File[],
            invoice: [] as File[],
            packing_list: [] as File[],
            certificate_of_origin: [] as File[],
            certificate_of_safety: [] as File[],
            delayCommentId: null as number | null,
        })

        const state_form$ = useVuelidate(
            {
                stamp: { required: requiredIf(() => props.ftlType === 1) },
                invoice: { required: requiredIf(() => props.ftlType === 1) },
                packing_list: { required: requiredIf(() => props.ftlType === 1) },
                certificate_of_origin: { required: requiredIf(() => props.ftlType === 1) },
                delayCommentId: { required: requiredIf(() => props.isComment) },
            },
            payload
        )

        const emitCancel = () => {
            if (props.isLoading) return
            emit('cancel')
        }

        const emitSubmit = async () => {
            if (props.isLoading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const formData: OnTheWayPayload = {
                id: Number(props.orderId),
                status_code: 'on_the_way',
                ftl_type: props.ftlType,
            }

            if (payload.certificate_of_safety.length) {
                formData.certificate_of_safety = payload.certificate_of_safety[0]
            }

            if (payload.stamp.length) {
                formData.stamp = payload.stamp[0]
            }
            if (payload.invoice.length) {
                formData.invoice = payload.invoice[0]
            }
            if (payload.packing_list.length) {
                formData.packing_list = payload.packing_list[0]
            }
            if (payload.certificate_of_origin.length) {
                formData.certificate_of_origin = payload.certificate_of_origin[0]
            }
            if (payload.delayCommentId) {
                formData.delayCommentId = payload.delayCommentId
            }

            emit('submit', getFormData(formData))
        }

        const data_state = reactive({
            comments: {
                is_loading: false,
                is_failed: false,
                list: [] as TripComment[],
            },
        })

        const loadComments = async () => {
            if (!props.stateId) return

            try {
                data_state.comments.is_loading = true

                const response = await FTL.getComments(props.stateId)
                data_state.comments.list = response.data

                data_state.comments.is_failed = false
            } catch (error) {
                data_state.comments.is_failed = true
                console.error(error)
            } finally {
                data_state.comments.is_loading = false
            }
        }

        onMounted(() => {
            loadComments()
        })

        return {
            payload,
            data_state,
            state_form$,
            emitCancel,
            emitSubmit,
            loadComments,
        }
    },
})
